import {objectFromEntries, entry} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState, useCallback, useEffect} from 'react';

import {NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import BenchlingConfig from 'BenchlingConfig';
import DataConnector from 'connect/DataConnector';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {DEFAULT_PAGE_SIZE} from 'nav/sidepanel/PageSizeUtil';
import {AssayRunSchema} from 'results/assay-schemas/AssaySchemaConstants';
import {AssayRun} from 'results/assays/AssayRecordConstants';
import {listAssayRuns} from 'results/assays/AssayRecordDAO';
import {fetchAllAssaySchemas} from 'results/AssaySchemaActions';
import AssayStore from 'results/AssayStore';
import {EntriesByRunId} from 'runs/inbox/RunsInboxModalConstants';
import FiltersContainer, {useSecondaryDropdownState} from 'search/filters/FiltersContainer';
import {getEmptyFilterErrors} from 'search/filters/ValidateFilters';
import {getSelectedSchema} from 'search/FilterUtil';
import {populateSearchParamWithDefaultSort} from 'search/getSortsOrDefault';
import {Filter, FilterKey, keyToEmptyFilter, SearchParams, SearchType} from 'search/SearchConstants';
import {sanitizeFilters} from 'search/SearchUtil';
import {ChipSize} from 'ui-library/chips/Chip';
import EntryChip from 'ui-library/chips/EntryChip';
import RunChip from 'ui-library/chips/RunChip';
import useOnMount from 'ui-library/hooks/useOnMount';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import * as DateUtil from 'util/DateUtil';

function getDefaultFilters(): Array<Filter> {
  const filters: Array<Filter> = [
    {
      ...keyToEmptyFilter(FilterKey.TYPES),
      value: [ModelTypeConstants.ASSAY_RUN],
    },
    {
      ...keyToEmptyFilter(FilterKey.ARCHIVE_PURPOSES),
      value: [NOT_ARCHIVED_PURPOSE_STRING],
    },
  ];
  return filters;
}

function getSearchParams(offset: number, filters: Array<Filter>, connectionId: string): SearchParams {
  const searchParams = {
    offset,
    limit: DEFAULT_PAGE_SIZE,
    query: '',
    filters: [
      ...filters,
      {
        ...keyToEmptyFilter(FilterKey.CONNECTION),
        value: [connectionId],
      },
    ],
    sorts: [],
    groupBy: null,
  };
  return populateSearchParamWithDefaultSort(searchParams, SearchType.RUN);
}

export interface ConnectionRunsProps {
  connectionId: string;
}
interface EnhancedProps {
  runSchemasById: {[id: string]: AssayRunSchema};
}

class ConnectionRunsConnector extends DataConnector<ConnectionRunsProps, EnhancedProps> {
  stores = [AssayStore];

  enhanceProps(): EnhancedProps {
    const runSchemas = AssayStore.getRunSchemas() ?? [];
    return {
      runSchemasById: objectFromEntries(runSchemas.map((schema) => entry(schema.id, schema))),
    };
  }
}

function ConnectionRuns(props: ConnectionRunsProps & EnhancedProps): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<Array<Filter>>(getDefaultFilters());
  const [assayRuns, setAssayRuns] = useState<Array<AssayRun>>([]);
  const [entriesByRunId, setEntriesByRunId] = useState<EntriesByRunId>({});
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [canFetchMore, setCanFetchMore] = useState(false);
  const secondaryDropdownState = useSecondaryDropdownState();
  const fetchData = useCallback(
    async (offset) => await listAssayRuns(getSearchParams(offset, filters, props.connectionId), 'read'),
    [props.connectionId, filters]
  );
  useOnMount(() => {
    fetchAllAssaySchemas();
  });

  useEffect(() => {
    async function fetchInitialData(): Promise<void> {
      setIsLoading(true);
      const results = await fetchData(0);
      setAssayRuns(results.assayRuns);
      setEntriesByRunId(results.entriesByRunId);
      setOffset(DEFAULT_PAGE_SIZE);
      setCanFetchMore(results.total > DEFAULT_PAGE_SIZE);
      setIsLoading(false);
    }
    fetchInitialData();
  }, [fetchData, setAssayRuns]);

  const selectedSchema = getSelectedSchema(filters, Object.values(props.runSchemasById));

  return (
    <div className="react-tab-container">
      <div>
        <div className="u-flex-display u-spacing-bottom">
          <FiltersContainer
            primaryFilterOptions={[
              {
                filterKey: FilterKey.TYPES,
                extraProps: {
                  availableTypes: [ModelTypeConstants.ASSAY_RUN],
                  isTypeRequired: true,
                },
              },
              {
                filterKey: FilterKey.ARCHIVE_PURPOSES,
                extraProps: {archivePurposes: BenchlingConfig.ARCHIVE_RUN_AND_RESULT_PURPOSES},
              },
            ]}
            secondaryFilterProps={{
              filterGroups: [
                {
                  name: 'General',
                  filterOptions: [
                    {filterKey: FilterKey.CREATED_AT},
                    {filterKey: FilterKey.MODIFIED_AT},
                    {filterKey: FilterKey.CREATOR},
                    {
                      filterKey: FilterKey.SCHEMA_FIELD,
                      extraProps: {
                        allowBlobLinkFields: true,
                        schemaFields: selectedSchema ? selectedSchema.fields : [],
                      },
                    },
                  ],
                },
              ],
              ...secondaryDropdownState,
            }}
            filters={filters}
            onChangeFilters={(newFilters: Array<Filter>) => {
              setFilters(sanitizeFilters(newFilters, getEmptyFilterErrors(newFilters)));
            }}
            selectedSchema={selectedSchema}
          />
        </div>
      </div>
      {isLoading ? (
        <CenteredHorizontalThrobber />
      ) : (
        <>
          <div className={css(styles.tableContainer)}>
            <Table
              emptyTableText="No runs"
              numRows={assayRuns.length}
              overflowVisible={true}
              className={css(styles.table)}
              getRowClassName={() => css(styles.tableRow)}
            >
              <Column
                header="Run"
                renderCell={(rowIndex: number) => (
                  <Cell
                    className={classNames({
                      'u-faded': assayRuns[rowIndex].archiveRecord != null,
                    })}
                  >
                    <RunChip id={assayRuns[rowIndex].apiId} size={ChipSize.SMALL} />
                  </Cell>
                )}
              />
              <Column
                header="Schema"
                renderCell={(rowIndex: number) => (
                  <Cell
                    className={classNames({
                      'u-faded': assayRuns[rowIndex].archiveRecord != null,
                    })}
                  >
                    {props.runSchemasById[assayRuns[rowIndex].schemaId]?.name}
                  </Cell>
                )}
              />
              <Column
                header="Created At"
                renderCell={(rowIndex: number) => (
                  <Cell
                    className={classNames({
                      'u-faded': assayRuns[rowIndex].archiveRecord != null,
                    })}
                  >
                    {DateUtil.formalTime(assayRuns[rowIndex].createdAt)}
                  </Cell>
                )}
              />
              <Column
                header="Origin"
                renderCell={(rowIndex: number) => (
                  <Cell
                    className={classNames({
                      'u-faded': assayRuns[rowIndex].archiveRecord != null,
                    })}
                  >
                    {entriesByRunId[assayRuns[rowIndex].id] ? (
                      <EntryChip id={entriesByRunId[assayRuns[rowIndex].id].id} size={ChipSize.SMALL} />
                    ) : (
                      assayRuns[rowIndex].originType
                    )}
                  </Cell>
                )}
              />
            </Table>
            {canFetchMore && (
              <div className="u-text-align-center u-small-spacing">
                {isLoadingMore ? (
                  <span className="u-faded">Loading...</span>
                ) : (
                  <a
                    onClick={async () => {
                      setIsLoadingMore(true);
                      const results = await fetchData(offset);
                      setAssayRuns([...assayRuns, ...results.assayRuns]);
                      setEntriesByRunId({...entriesByRunId, ...results.entriesByRunId});
                      setOffset(offset + DEFAULT_PAGE_SIZE);
                      setCanFetchMore(results.total > offset + DEFAULT_PAGE_SIZE);
                      setIsLoadingMore(false);
                    }}
                  >
                    Load more runs
                  </a>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default ConnectionRunsConnector.connect(ConnectionRuns);

const styles = StyleSheet.create({
  tableContainer: {
    maxHeight: '400px',
    overflowY: 'auto',
  },
  table: {
    borderLeft: 0,
    borderRight: 0,
    marginBottom: 0,
    marginRight: 0,
    marginLeft: 0,
  },
  tableRow: {
    height: '36px',
  },
  tableCell: {
    padding: '0 13px',
  },
  checkboxCell: {
    textAlign: 'center',
    padding: 0,
    width: '20px',
  },
});
